import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import Frame from "@/components/Frame";
import { linkify } from "@/components/Linkify";
import { FadeIn, MaskedLine, MaskedLineScroll, Overline } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const RELATED_SERVICES = [
  { to: "/reparation-toiture-94", label: "Réparation de toiture 94" },
  { to: "/fuite-toiture-94", label: "Fuite de toiture 94" },
  { to: "/renovation-toiture-94", label: "Rénovation de toiture 94" },
  { to: "/zinguerie-94", label: "Zinguerie 94" },
  { to: "/gouttiere-94", label: "Gouttières 94" },
  { to: "/demoussage-toiture-94", label: "Démoussage 94" },
];

const NEIGHBORS = {
  "couvreur-joinville-le-pont": [
    { to: "/couvreur-champigny-sur-marne", label: "Champigny-sur-Marne" },
    { to: "/couvreur-nogent-sur-marne", label: "Nogent-sur-Marne" },
  ],
  "couvreur-saint-maur-des-fosses": [
    { to: "/couvreur-champigny-sur-marne", label: "Champigny-sur-Marne" },
    { to: "/couvreur-joinville-le-pont", label: "Joinville-le-Pont" },
  ],
  "couvreur-nogent-sur-marne": [
    { to: "/couvreur-le-perreux-sur-marne", label: "Le Perreux-sur-Marne" },
    { to: "/couvreur-joinville-le-pont", label: "Joinville-le-Pont" },
  ],
  "couvreur-le-perreux-sur-marne": [
    { to: "/couvreur-nogent-sur-marne", label: "Nogent-sur-Marne" },
    { to: "/couvreur-bry-sur-marne", label: "Bry-sur-Marne" },
  ],
  "couvreur-villiers-sur-marne": [
    { to: "/couvreur-le-plessis-trevise", label: "Le Plessis-Trévise" },
    { to: "/couvreur-chennevieres-sur-marne", label: "Chennevières-sur-Marne" },
  ],
  "couvreur-bry-sur-marne": [
    { to: "/couvreur-le-perreux-sur-marne", label: "Le Perreux-sur-Marne" },
    { to: "/couvreur-villiers-sur-marne", label: "Villiers-sur-Marne" },
  ],
  "couvreur-chennevieres-sur-marne": [
    { to: "/couvreur-saint-maur-des-fosses", label: "Saint-Maur-des-Fossés" },
    { to: "/couvreur-le-plessis-trevise", label: "Le Plessis-Trévise" },
  ],
  "couvreur-le-plessis-trevise": [
    { to: "/couvreur-villiers-sur-marne", label: "Villiers-sur-Marne" },
    { to: "/couvreur-chennevieres-sur-marne", label: "Chennevières-sur-Marne" },
  ],
};

export default function CityPage({ page }) {
  const currentPath = `/${page.slug}`;
  const usedLinks = new Set();
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Couvreur 94", to: "/couvreur-94" },
    { label: page.ville },
  ];
  const nearby = NEIGHBORS[page.slug] || [];
  return (
    <>
      <Seo title={page.metaTitle} description={page.metaDescription} path={`/${page.slug}`} />
      <Breadcrumb items={crumbs} />
      <section className="bg-midnight text-white grain relative overflow-hidden">
        <div className="absolute inset-0 blueprint-dark opacity-60" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
          <MaskedLine delay={0.05}>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Couvreur — Val-de-Marne (94)</span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.95] text-3xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="city-page-title">
            <MaskedLine delay={0.18}>{page.h1}</MaskedLine>
          </h1>
          <FadeIn delay={0.45} className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="tel:0646300391"
              data-testid="city-call-button"
              className="btn-sweep inline-flex items-center justify-center gap-3 whitespace-nowrap bg-signal text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              <Phone size={18} strokeWidth={1.5} />
              Appeler maintenant
            </a>
            <Link
              to="/#contact"
              data-testid="city-quote-button"
              className="btn-sweep btn-sweep-light inline-flex items-center justify-center gap-3 whitespace-nowrap border border-white/40 text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              Demander un devis gratuit
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6">
            {page.intro.map((p, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <p className="text-base md:text-lg text-midnight/75 leading-relaxed">{linkify(p, usedLinks, currentPath)}</p>
              </FadeIn>
            ))}
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <FadeIn delay={0.2}>
              <Frame label={`PHOTO — ${page.ville.toUpperCase()}`} ratio="aspect-[4/5]" src={page.photo} alt={page.photoAlt} />
            </FadeIn>
          </div>
        </div>
      </section>

      {page.blocks.map((block, i) => (
        <section key={block.h2} className={`py-16 md:py-20 border-t border-midnight/10 ${i % 2 === 1 ? "bg-white" : "bg-bone"}`}>
          <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="font-mono text-sm text-signal tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
              <FadeIn delay={0.05}>
                <h2 className="mt-3 font-display font-black uppercase tracking-tighter leading-tight text-2xl md:text-3xl">{block.h2}</h2>
              </FadeIn>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-5">
              {block.paragraphs.map((p, j) => (
                <FadeIn key={j} delay={j * 0.06}>
                  <p className="text-base text-midnight/70 leading-relaxed">{linkify(p, usedLinks, currentPath)}</p>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="bg-midnight text-white py-20 grain relative">
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline dark>Pourquoi nous</Overline>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              <MaskedLineScroll>Pourquoi contacter</MaskedLineScroll>
              <MaskedLineScroll delay={0.12}><span className="text-signal">MSC Toitures</span> ?</MaskedLineScroll>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="border-t border-white/10">
              {page.whyPoints.map((point, i) => (
                <FadeIn key={i} delay={i * 0.05}>
                  <li className="flex items-center gap-4 py-4 border-b border-white/10" data-testid={`city-why-${i}`}>
                    <span className="w-1.5 h-1.5 bg-signal shrink-0" />
                    <span className="text-sm md:text-base text-white/80">{point}</span>
                  </li>
                </FadeIn>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-bone py-20 border-b border-midnight/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline>FAQ locale</Overline>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter leading-tight text-2xl md:text-3xl">
              <MaskedLineScroll>Vos questions à {page.ville}</MaskedLineScroll>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Accordion type="single" collapsible className="border-t border-midnight/10" data-testid="city-faq">
              {page.faq.map((item, i) => (
                <AccordionItem key={i} value={`cf-${i}`} className="border-midnight/10">
                  <AccordionTrigger
                    data-testid={`city-faq-question-${i}`}
                    className="py-5 text-left font-display font-extrabold uppercase tracking-tight text-base hover:text-signal hover:no-underline transition-colors duration-300"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-midnight/70 leading-relaxed" data-testid={`city-faq-answer-${i}`}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 border-b border-midnight/10" data-testid="city-contact-block">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Overline>Besoin d'un couvreur à {page.ville} ?</Overline>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              <MaskedLineScroll>Contactez MSC Toitures</MaskedLineScroll>
            </h2>
            <div className="mt-6 flex items-start gap-4">
              <MapPin size={20} strokeWidth={1.5} className="text-signal mt-1 shrink-0" />
              <p className="text-midnight/70">
                <strong className="text-midnight">MSC Toitures</strong><br />
                34 Bd de Polangis, 94500 Champigny-sur-Marne<br />
                <a href="tel:0646300391" data-testid="city-phone-1" className="hover:text-signal transition-colors duration-300">06 46 30 03 91</a>
                {" — "}
                <a href="tel:0187078662" data-testid="city-phone-2" className="hover:text-signal transition-colors duration-300">01 87 07 86 62</a>
              </p>
            </div>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-signal">Déplacement gratuit — Devis gratuit</p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-col gap-4">
            <a
              href="tel:0646300391"
              data-testid="city-cta-call"
              className="btn-sweep flex items-center justify-center gap-3 whitespace-nowrap bg-signal text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              <Phone size={18} strokeWidth={1.5} />
              Appeler MSC Toitures
            </a>
            <Link
              to="/#contact"
              data-testid="city-cta-quote"
              className="btn-sweep flex items-center justify-center gap-3 whitespace-nowrap bg-midnight text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-bone py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel mb-8">Voir également</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 border-t border-l border-midnight/10">
            {[
              { to: "/couvreur-94", label: "Couvreur 94" },
              { to: "/couvreur-champigny-sur-marne", label: "Couvreur Champigny-sur-Marne" },
              ...RELATED_SERVICES.slice(0, 2),
              ...nearby,
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`city-related-${l.to.slice(1)}`}
                className="group border-r border-b border-midnight/10 p-6 flex items-center justify-between gap-4 hover:bg-white transition-colors duration-300"
              >
                <span className="font-display font-extrabold uppercase tracking-tight text-sm">{l.label}</span>
                <ArrowRight size={16} strokeWidth={1.5} className="text-signal shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
